import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, TrendingUp, ShieldAlert } from "lucide-react";

export default function AIAssistant() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");

  const call = async (endpoint, kind) => {
    setBusy(kind);
    setResult({ kind, data: null, loading: true });
    try {
      const { data } = await api.post(endpoint);
      setResult({ kind, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service error");
      setResult(null);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-page">
      <div>
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
          <Sparkles size={12} /> Powered by Claude Sonnet 4.6
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight">AI Operations Assistant</h1>
        <p className="text-sm text-muted-foreground">Get instant recommendations on dispatch, demand and fraud.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <AiCard
          icon={Zap} title="Dispatch Assistant"
          desc="Read pending orders + available bowsers/drivers, recommend optimal assignments prioritizing emergencies and capacity."
          onRun={() => call("/ai/dispatch-suggestions", "dispatch")}
          loading={busy === "dispatch"}
          testId="btn-ai-dispatch"
        />
        <AiCard
          icon={TrendingUp} title="Demand Forecast"
          desc="Analyse last 30 days of orders and predict next 7 days of diesel demand per customer."
          onRun={() => call("/ai/demand-forecast", "forecast")}
          loading={busy === "forecast"}
          testId="btn-ai-forecast"
        />
        <AiCard
          icon={ShieldAlert} title="Fraud Scan"
          desc="Scan recent delivered orders for suspicious meter/quantity/density patterns."
          onRun={() => call("/ai/fraud-scan", "fraud")}
          loading={busy === "fraud"}
          testId="btn-ai-fraud"
        />
      </div>

      {result && (
        <Card data-testid="ai-result">
          <CardHeader>
            <CardTitle className="capitalize">{result.kind} · AI Result</CardTitle>
          </CardHeader>
          <CardContent>
            {result.loading && <div className="py-8 text-center text-sm text-muted-foreground">Analysing… Claude is thinking.</div>}
            {!result.loading && result.data && <AIRenderer kind={result.kind} data={result.data} />}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const AiCard = ({ icon: Icon, title, desc, onRun, loading, testId }) => (
  <Card>
    <CardContent className="flex flex-col gap-3 p-6">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon size={18} /></div>
      <div className="font-display text-lg font-bold">{title}</div>
      <div className="text-sm text-muted-foreground">{desc}</div>
      <Button onClick={onRun} disabled={loading} className="mt-2 pill-btn" data-testid={testId}>
        {loading ? "Running…" : "Run analysis"}
      </Button>
    </CardContent>
  </Card>
);

function AIRenderer({ kind, data }) {
  if (kind === "dispatch") {
    const items = data.suggestions || [];
    return (
      <div className="space-y-2">
        {data.notes && <div className="rounded border border-primary/30 bg-primary/5 p-3 text-sm">{data.notes}</div>}
        {items.length === 0 && <div className="py-4 text-center text-sm text-muted-foreground">{data.message || "No suggestions available."}</div>}
        {items.map((s) => (
          <div key={s.order_code} className="rounded border border-border/60 p-3 text-sm">
            <div className="flex items-center gap-2 font-mono font-bold">
              {s.order_code} → {s.bowser_code} + {s.driver_code}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{s.reasoning}</div>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "forecast") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard label="Next-week total" value={`${(data.next_week_total_litres || 0).toLocaleString("en-IN")} L`} />
          <StatCard label="Confidence" value={data.confidence || "—"} />
          <StatCard label="Top customers" value={(data.top_customers || []).length} />
        </div>
        {data.notes && <div className="rounded border border-primary/30 bg-primary/5 p-3 text-sm">{data.notes}</div>}
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="py-2">Customer</th><th className="py-2 text-right">Predicted litres</th><th className="py-2">Reasoning</th></tr>
          </thead>
          <tbody>
            {(data.top_customers || []).map((c) => (
              <tr key={c.customer} className="border-b border-border/40">
                <td className="py-2 font-semibold">{c.customer}</td>
                <td className="py-2 text-right font-mono">{c.predicted_litres?.toLocaleString("en-IN")}</td>
                <td className="py-2 text-xs text-muted-foreground">{c.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (kind === "fraud") {
    const alerts = data.alerts || [];
    return (
      <div className="space-y-2">
        {data.notes && <div className="rounded border border-primary/30 bg-primary/5 p-3 text-sm">{data.notes}</div>}
        {alerts.length === 0 && <div className="py-4 text-center text-sm text-status-delivered">✓ No suspicious patterns found.</div>}
        {alerts.map((a) => (
          <div key={a.order_code} className={`rounded border p-3 text-sm ${a.severity === "high" ? "border-status-cancelled/40 bg-status-cancelled/5" : a.severity === "medium" ? "border-status-pending/40 bg-status-pending/5" : "border-border/60"}`}>
            <div className="font-mono font-bold">{a.order_code} · {a.severity?.toUpperCase()}</div>
            <div className="mt-1 text-xs text-muted-foreground">{a.reason}</div>
          </div>
        ))}
      </div>
    );
  }
  return <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>;
}

const StatCard = ({ label, value }) => (
  <div className="rounded border border-border/60 p-3">
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-xl font-black capitalize">{value}</div>
  </div>
);
